import styled, { css } from 'styled-components';
import NextImage from 'next/image';

import Section from 'Elements/Section';
import { Col } from 'Components/Grid';

import IconButton from 'Campaigns/Clearing/Button/Icon';

import { ReactComponent as WaveSVG } from 'Public/graphics/waves/clearing/wave-hero.svg';

export const StyledSection = styled(Section)`
    display: flex;
    justify-content: center;
    z-index: 2;
`;

export const Wrapper = styled.div`
    ${({ theme }) => css`
        && {
            position: relative;
            background: ${theme.colors.campaigns.clearing.primary.hex};
            width: 100%;
            max-width: 96.5rem;
            margin: 2rem 1rem 0;
            padding: 0;

            @media ${theme.breakpoints.medium.media} {
                padding: 0 1rem;
            }

            @media ${theme.breakpoints.large.media} {
                margin: 2rem 2rem 0;
                padding: 0 2rem;
            }
        }
    `}
`;

export const InnerContent = styled.div<{ $isClearingOpen: boolean }>`
    ${({ theme, $isClearingOpen }) => css`
        padding: ${$isClearingOpen ? ' 0 0 2.25rem' : '0 0 1.25rem'};

        @media ${theme.breakpoints.medium.media} {
            padding: 3rem 0 1.5rem;
        }

        @media ${theme.breakpoints.large.media} {
            padding: ${$isClearingOpen ? ' 4rem 0 5rem' : '3rem 0 1rem'};
        }
    `}
`;

export const PreHeading = styled.span`
    ${({ theme }) => css`
        font-size: 1.75rem;
        line-height: 1;
        text-transform: uppercase;
        color: ${theme.colors.campaigns.clearing.primaryContrast.hex};
        font-family: 'acumin-pro-extra-condensed', sans-serif;

        @media ${theme.breakpoints.large.media} {
            font-size: 2.5rem;
        }
    `}
`;

export const Heading = styled.h1`
    ${({ theme }) => css`
        font-size: 2.25rem;
        line-height: 1.2;
        font-weight: 800;
        color: ${theme.colors.campaigns.clearing.primaryContrast.hex};
        margin: 0;

        @media ${theme.breakpoints.medium.media} {
            padding-right: 1rem;
        }

        @media ${theme.breakpoints.large.media} {
            font-size: 5rem;
            padding-right: 0;
        }
    `}
`;

export const ClearingOpenWrapper = styled.div`
    ${({ theme }) => css`
        margin-top: 1rem;
        padding: 1.5rem 1rem;
        background-color: ${theme.colors.campaigns.clearing.primaryContrast.hex};
        font-size: 1.75rem;

        @media ${theme.breakpoints.large.media} {
            font-size: 1.75rem;
            margin-top: 2rem;
            padding: 1.5rem;
        }
    `}
`;

export const ContactsHeading = styled.div`
    ${({ theme }) => css`
        margin-top: 0;
        margin-bottom: 1rem;

        p:first-of-type {
            margin-top: 0;
            font-size: 1.25rem;
            font-weight: 500;

            @media ${theme.breakpoints.large.media} {
                font-size: 1.75rem;
            }
        }
    `}
`;

export const PhoneWrapper = styled.div`
    ${({ theme }) => css`
        margin-top: 1rem;
        display: flex;
        align-items: center;
        gap: 1rem;

        @media ${theme.breakpoints.medium.media} {
            gap: 0.75rem;
        }

        @media ${theme.breakpoints.large.media} {
            gap: 1.25rem;
        }
    `}
`;

export const LinesOpen = styled.span`
    ${({ theme }) => css`
        font-size: 1rem;
        color: ${theme.colors.campaigns.clearing.secondary.hex};

        @media ${theme.breakpoints.large.media} {
            font-size: 1.125rem;
        }
    `}
`;

export const ContactsWrapper = styled.ul`
    ${({ theme }) => css`
        margin: 1rem 0;
        padding: 0;
        display: flex;
        flex-wrap: wrap;
        gap: 0.75rem;
        list-style: none;

        @media ${theme.breakpoints.large.media} {
            margin: 1.5rem 0;
            gap: 1rem;
        }
    `}
`;

export const ContactItem = styled.li`
    ${({ theme }) => css`
        margin: 0;
        width: 100%;

        @media only screen and (min-width: 23.4375rem) {
            width: calc(50% - 0.5rem);
        }

        @media ${theme.breakpoints.medium.media} {
            width: unset;
        }
    `}
`;

export const ClearingPromiseWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
`;

export const StyledIcon = styled.svg`
    ${({ theme }) => css`
        height: 1.5rem;
        width: 1.5rem;
        color: ${theme.colors.campaigns.clearing.primary.hex};
    `}
`;

export const ClearingPromiseText = styled.p`
    margin: 0;
    font-weight: 500;
`;

export const StyledIconButton = styled(IconButton)`
    && {
        width: 100%;

        @media only screen and (max-width: 23.4375rem) {
            gap: 0.5rem;
        }

        @media only screen and (max-width: 25rem) {
            padding: 0.75rem 0.225rem;
        }
    }
`;

export const WaveWrapper = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
`;

export const StyledWave = styled(WaveSVG)`
    ${({ theme }) => css`
        position: absolute;
        width: 67.0625rem;
        height: 12.9375rem;
        left: -14rem;
        top: 7.5rem;

        @media ${theme.breakpoints.mediumSmall.media} {
            width: 62.5rem;
            height: 31.25rem;
            left: 0;
            top: -2rem;
        }

        @media ${theme.breakpoints.large.media} {
            width: 62.5rem;
            height: 31.25rem;
            left: 0;
            top: -2rem;
        }
    `}
`;

export const ImageWrapper = styled.div`
    ${({ theme }) => css`
        position: relative;
        width: 100%;
        height: 8.5rem;
        top: -1.5rem;

        @media ${theme.breakpoints.medium.media} {
            height: 100%;
        }

        @media ${theme.breakpoints.large.media} {
            height: calc(100% + 3rem);
        }
    `}
`;

export const ContainerIframe = styled.div`
    ${({ theme }) => css`
        position: relative;
        width: 100%;
        background: ${theme.colors.secondary.tints[8].hex};
        padding: 1rem;
        margin: 2rem 0;
        border-radius: 0.5rem;

        @media ${theme.breakpoints.medium.media} {
            width: 90%;
        }
    `}
`;

export const StyledCol = styled(Col)`
    ${({ theme }) => css`
        order: 2;

        @media ${theme.breakpoints.medium.media} {
            order: 1;
        }
    `}
`;

export const StyledCol2 = styled(Col)`
    ${({ theme }) => css`
        order: 1;

        @media ${theme.breakpoints.medium.media} {
            order: 2;
        }
    `}
`;

export const MobileImage = styled(NextImage)`
    ${({ theme }) => css`
        @media ${theme.breakpoints.medium.media} {
            display: none;
        }
    `}
`;

export const DesktopImage = styled(NextImage)`
    ${({ theme }) => css`
        display: none;

        @media ${theme.breakpoints.medium.media} {
            display: block;
        }
    `}
`;
